import React from 'react';

interface CitationLinkProps {
  /** Azure Search ID of the cited chunk */
  citationId: string;
  /** Display number for the citation (1, 2, 3, etc.) */
  number: number;
  /** Optional tooltip text shown on hover */
  tooltip?: string;
  /** Callback when citation is clicked */
  onClick: (citationId: string) => void;
}

/**
 * CitationLink - Renders a clickable superscript citation number.
 *
 * When clicked, opens the source document with the cited chunk highlighted.
 * Citations fail gracefully - if the chunk can't be found, clicking does nothing.
 */
export const CitationLink: React.FC<CitationLinkProps> = ({
  citationId,
  number,
  tooltip,
  onClick,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick(citationId);
  };

  return (
    <span
      onClick={handleClick}
      className="citation-link"
      title={tooltip || `View source (citation ${number})`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(citationId);
        }
      }}
      style={{
        cursor: 'pointer',
        color: '#ffffff',
        backgroundColor: '#3b82f6',
        fontWeight: 600,
        fontSize: '0.75em',
        verticalAlign: 'baseline',
        position: 'relative',
        top: '-0.15em',
        marginLeft: '2px',
        marginRight: '1px',
        padding: '1px 5px',
        borderRadius: '4px',
        transition: 'all 0.15s ease',
        userSelect: 'none',
        display: 'inline-block',
        lineHeight: '1.2',
        minWidth: '16px',
        textAlign: 'center',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#2563eb';
        e.currentTarget.style.transform = 'scale(1.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#3b82f6';
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      {number}
    </span>
  );
};

export default CitationLink;
